import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";

const DEFAULT = {
  status: "fresh",
  dietary_tag: "all",
  calorie_range: "all",
  // "all" = every storage location the user can see. Otherwise this is the
  // location.id selected on the Filters page; the Filters UI keeps the
  // option dynamic so deleting a location doesn't leave a stale filter
  // selection sticking around forever.
  location_id: "all",
  sort: "expiring_soon",
};

const KEY = "qfresh_filters";

const FilterContext = createContext(null);

export const FilterProvider = ({ children }) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? { ...DEFAULT, ...JSON.parse(raw) } : DEFAULT;
    } catch {
      return DEFAULT;
    }
  });

  // Reset filters to defaults whenever a new auth session starts (login OR
  // register). We track the user id so that React doesn't reset on every
  // unrelated user-object reference change (e.g. updateMe patches).
  const lastUserId = useRef(user?.id ?? null);
  useEffect(() => {
    const id = user?.id ?? null;
    if (id && id !== lastUserId.current) {
      setFilters(DEFAULT);
    }
    lastUserId.current = id;
  }, [user?.id]);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(filters));
  }, [filters]);

  const reset = () => setFilters(DEFAULT);

  // Count of fields different from defaults — used for the bottom-nav badge
  const activeCount = Object.keys(DEFAULT).reduce(
    (n, k) => n + (filters[k] !== DEFAULT[k] ? 1 : 0),
    0
  );

  return (
    <FilterContext.Provider value={{ filters, setFilters, reset, activeCount, DEFAULT }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => {
  const v = useContext(FilterContext);
  if (!v) throw new Error("useFilters must be used inside FilterProvider");
  return v;
};

export const filtersToParams = (filters) => {
  const params = { sort: filters.sort };
  if (filters.status !== "all") params.status = filters.status;
  if (filters.dietary_tag !== "all") params.dietary_tag = filters.dietary_tag;
  if (filters.location_id && filters.location_id !== "all") {
    params.location_id = filters.location_id;
  }
  if (filters.calorie_range === "under-300") params.max_calories = 299;
  else if (filters.calorie_range === "300-500") {
    params.min_calories = 300;
    params.max_calories = 500;
  } else if (filters.calorie_range === "500-plus") params.min_calories = 501;
  return params;
};
